import { Injectable, Signal, computed, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, map, tap } from 'rxjs';

import { RouterFacade } from '@core/shared/data-access';
import { ConditionArticle } from '@mp/contract-management/common/domain';
import {
  ConditionSigningDetailsFacade,
  ConditionSigningDetailsState,
} from '@mp/contract-management/condition-signing-details/data-access';
import { ConditionSigningOverview } from '@mp/contract-management/condition-signing-details/domain';
import { ContractMailingAddressWithCountryName } from '@mp/contract-management/condition-signing-details/ui';
import { CountriesFacade } from '@mpomd/shared/data-access';

import { CONDITION_ID_PARAM_NAME } from '../../route-params';

export interface ConditionSigningDetailsOverviewState {
  conditionId: string;
}

export const INITIAL_STATE: ConditionSigningDetailsOverviewState = {
  conditionId: '',
};

@Injectable()
export class ConditionSigningDetailsOverviewStore extends ComponentStore<ConditionSigningDetailsOverviewState> {
  private readonly countriesFacade = inject(CountriesFacade);
  private readonly countries = this.countriesFacade.countries;
  private readonly conditionSigningDetailsFacade = inject(ConditionSigningDetailsFacade);
  private readonly routerFacade = inject(RouterFacade);
  private readonly activeRoute = inject(ActivatedRoute);

  private readonly conditionSigningDetailsState: Signal<ConditionSigningDetailsState> =
    this.conditionSigningDetailsFacade.conditionSigningDetailsState;

  readonly conditionSigningOverview: Signal<ConditionSigningOverview | undefined> = computed(
    () => this.conditionSigningDetailsState().conditionSigningOverview,
  );

  readonly conditionCountry: Signal<ContractMailingAddressWithCountryName | undefined> = computed(() =>
    this.mapToContractMailingAddressWithCountryName(this.conditionSigningDetailsState().conditionSigningOverview),
  );

  readonly conditionArticles: Signal<ConditionArticle[] | undefined> = computed(
    () => this.conditionSigningDetailsState().conditionArticles,
  );

  readonly conditionSigningOverviewLoaded: Signal<boolean> = computed(
    () => this.conditionSigningDetailsState().conditionSigningOverviewLoaded,
  );

  constructor() {
    super(INITIAL_STATE);

    this.countriesFacade.fetchCountries();
    this.initSelectConditionIdScopeEffect();
  }

  navigateBack(): void {
    this.routerFacade.back();
  }

  // TODO: PM - This simulates a reset for now. Should be adjusted later
  signCondition(): void {
    this.conditionSigningDetailsFacade.signCondition();
  }

  // TODO: PM - This simulates a reset for now. Should be adjusted later
  requestConfirmation(): void {
    this.conditionSigningDetailsFacade.requestConfirmation();
  }

  // TODO: PM - This simulates a reset for now. Should be adjusted later
  rejectCondition(): void {
    this.conditionSigningDetailsFacade.fetchConditionSigningOverview('');
  }

  private readonly setSelectedConditionId = this.updater(
    (state, conditionId: string): ConditionSigningDetailsOverviewState => ({
      ...state,
      conditionId,
    }),
  );

  private initSelectConditionIdScopeEffect(): void {
    const selectedConditionIdSource$: Observable<string> = this.activeRoute.params.pipe(
      map((params) => params[CONDITION_ID_PARAM_NAME] ?? ''),
    );

    this.effect((conditionId$: Observable<string>) => {
      return conditionId$.pipe(
        tap((conditionId: string) => this.setSelectedConditionId(conditionId)),
        tap((conditionId) => this.fetchConditionSigningOverview(conditionId)),
      );
    })(selectedConditionIdSource$);
  }

  private fetchConditionSigningOverview(conditionId: string): void {
    this.conditionSigningDetailsFacade.fetchConditionSigningOverview(conditionId);
  }

  private mapToContractMailingAddressWithCountryName(
    conditionSigningOverview: ConditionSigningOverview | undefined,
  ): ContractMailingAddressWithCountryName | undefined {
    if (!conditionSigningOverview) {
      return undefined;
    }

    const countryCode = conditionSigningOverview.address.countryCode;

    return {
      ...conditionSigningOverview.address,
      countryName: this.countries().find((country) => country.countryCode === countryCode)?.name ?? countryCode,
    };
  }
}
